import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages a size guide modal.
 * Handles opening/closing the dialog and tab switching.
 * Extends DialogComponent but prevents closing on backdrop click or Escape key.
 *
 * @extends {DialogComponent}
 */
class SizeGuideComponent extends DialogComponent {

  connectedCallback() {
    super.connectedCallback();

    if (!this.querySelector('template[data-size-guide-body]')) {
      this.setupTabs();
      this.setupUnitToggle();
    }
  }

  /**
   * IW-PDP-09: charts stay inert in <template> until the first open so the
   * 16 tables are not in the live tree on every product page.
   */
  hydrateBody() {
    const template = this.querySelector('template[data-size-guide-body]');

    if (!(template instanceof HTMLTemplateElement)) return;

    const parent = template.parentNode;

    if (!parent) return;

    parent.insertBefore(template.content, template);
    template.remove();
    this.setupTabs();
    this.setupUnitToggle();
  }

  open() {
    this.hydrateBody();
    this.showDialog();
  }

  /**
   * Closes the size guide modal.
   * Prevents default Escape key behavior (dialog should only close via X button).
   * @param {MouseEvent} event - The mouse event.
   */
  close(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  }

  /**
   * Sets up tab switching: primary tabs (Size Chart / How To Measure).
   */
  setupTabs() {
    this.setupPrimaryTabs();
    this.setupChartTabs();
  }

  /**
   * Primary tabs: Size Chart vs How To Measure. Switches visible panel.
   */
  setupPrimaryTabs() {
    const primaryTabs = this.querySelectorAll('.size-guide-modal__primary-tab');
    const panels = this.querySelectorAll('.size-guide-modal__panel');

    primaryTabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const panelId = tab.dataset.primaryTab;

        primaryTabs.forEach((t) => {
          t.classList.remove('size-guide-modal__primary-tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('size-guide-modal__primary-tab--active');
        tab.setAttribute('aria-selected', 'true');

        panels.forEach((panel) => {
          const isActive = panel.dataset.panel === panelId;
          panel.classList.toggle('size-guide-modal__panel--active', isActive);
          panel.hidden = !isActive;
        });
      });

      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const index = Array.from(primaryTabs).indexOf(tab);
        const next = event.key === 'ArrowRight' ? index + 1 : index - 1;
        const target = primaryTabs[(next + primaryTabs.length) % primaryTabs.length];
        target.focus();
        target.click();
      });
    });
  }

  setupChartTabs() {
    const sizeChartPanel = this.querySelector('#size-guide-panel-size-chart');
    if (!sizeChartPanel) return;

    const tabs = sizeChartPanel.querySelectorAll('.size-guide-modal__tab[role="tab"]');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const panelId = tab.getAttribute('aria-controls');
        if (!panelId) return;

        tabs.forEach((t) => {
          t.classList.remove('size-guide-modal__tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('size-guide-modal__tab--active');
        tab.setAttribute('aria-selected', 'true');

        sizeChartPanel.querySelectorAll('.size-guide-modal__subpanel').forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle('size-guide-modal__subpanel--active', isActive);
          panel.hidden = !isActive;
        });
      });

      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const index = Array.from(tabs).indexOf(tab);
        const next = event.key === 'ArrowRight' ? index + 1 : index - 1;
        const target = tabs[(next + tabs.length) % tabs.length];
        target.focus();
        target.click();
      });
    });
  }

  setupUnitToggle() {
    const sizeChartPanel = this.querySelector('#size-guide-panel-size-chart');
    if (!sizeChartPanel) return;

    const options = sizeChartPanel.querySelectorAll('.size-guide-modal__unit-option');
    const switchEl = sizeChartPanel.querySelector('.size-guide-modal__unit-switch');
    if (!options.length || !switchEl) return;

    const CM_TO_IN = 1 / 2.54;
    const NUMBER_PATTERN = /-?\d*\.?\d+/g;
    const CM_UNIT_PATTERN = /\b(?:centimetres?|centimeters?|cm)\b/gi;
    const IN_UNIT_PATTERN = /\b(?:inches|inch|in)\b/gi;
    const NON_MEASUREMENT_PATTERN = /[()\[\]{}.,\-\/\\:+%~\s]/g;

    const formatMeasurement = (value) => {
      if (!Number.isFinite(value)) return '';
      const roundedValue = Math.round(value * 10) / 10;

      if (Number.isInteger(roundedValue)) {
        return String(roundedValue);
      }

      return roundedValue.toFixed(1).replace(/\.0$/, '');
    };

    const normalizeMeasurementText = (value) => String(value ?? '').replace(/\u00a0/g, ' ').trim().replace(/\s+/g, ' ');

    const replaceUnits = (value, unit) => {
      if (unit === 'in') {
        return value.replace(CM_UNIT_PATTERN, 'in');
      }

      return value.replace(IN_UNIT_PATTERN, 'cm');
    };

    const extractNumericMatches = (value) => normalizeMeasurementText(value).match(NUMBER_PATTERN) || [];

    const isAutoConvertibleCell = (cell, text) => {
      if (!text || !/\d/.test(text)) return false;
      if (cell.closest('thead')) return false;

      const rowCells = Array.from(cell.parentElement?.children ?? []).filter(
        (element) => element.tagName === 'TD' || element.tagName === 'TH'
      );
      if (rowCells.indexOf(cell) === 0) return false;

      const residualText = text
        .toLowerCase()
        .replace(CM_UNIT_PATTERN, '')
        .replace(IN_UNIT_PATTERN, '')
        .replace(NUMBER_PATTERN, '')
        .replace(NON_MEASUREMENT_PATTERN, '');

      return residualText === '';
    };

    const buildInDisplay = (cmDisplay, cmSource) => {
      const displayTemplate = normalizeMeasurementText(cmDisplay);
      const sourceText = normalizeMeasurementText(cmSource);
      const sourceMatches = extractNumericMatches(sourceText);
      const numericMatches = sourceMatches.length ? sourceMatches : extractNumericMatches(displayTemplate);

      if (!numericMatches.length) {
        return replaceUnits(displayTemplate, 'in');
      }

      let numericIndex = 0;
      const convertedDisplay = displayTemplate.replace(NUMBER_PATTERN, () => {
        const rawValue = numericMatches[numericIndex++];
        const parsedValue = Number.parseFloat(rawValue);

        if (Number.isNaN(parsedValue)) {
          return rawValue;
        }

        return formatMeasurement(parsedValue * CM_TO_IN);
      });

      return replaceUnits(convertedDisplay, 'in');
    };

    const prepareConvertibleCell = (cell) => {
      if (cell.dataset.unitPrepared === 'true') {
        return cell.dataset.cmDisplay && cell.dataset.inDisplay;
      }

      const originalText = normalizeMeasurementText(cell.textContent);
      const cmSource = normalizeMeasurementText(cell.dataset.cm || originalText);
      const inSource = normalizeMeasurementText(cell.dataset.in);
      const canAutoConvert = !cell.dataset.cm && isAutoConvertibleCell(cell, originalText);

      if (!cell.dataset.cm && !cell.dataset.in && !canAutoConvert) {
        cell.dataset.unitPrepared = 'true';
        return false;
      }

      cell.dataset.cmDisplay = originalText || cmSource;
      cell.dataset.inDisplay = inSource || buildInDisplay(cell.dataset.cmDisplay, cmSource || cell.dataset.cmDisplay);
      cell.dataset.unitPrepared = 'true';

      return cell.dataset.cmDisplay && cell.dataset.inDisplay;
    };

    const getConvertibleCells = () => {
      const cells = Array.from(
        sizeChartPanel.querySelectorAll('.size-guide-modal__rte [data-cm], .size-guide-modal__rte [data-in], .size-guide-modal__table-container td')
      );

      return cells.filter((cell, index) => cells.indexOf(cell) === index).filter(prepareConvertibleCell);
    };

    const updateTable = (unit) => {
      const cells = getConvertibleCells();
      cells.forEach((cell) => {
        cell.textContent = unit === 'in' ? cell.dataset.inDisplay : cell.dataset.cmDisplay;
      });
    };

    getConvertibleCells();

    options.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const unit = btn.dataset.unit;
        options.forEach((b) => {
          const isActive = b.dataset.unit === unit;
          b.classList.toggle('size-guide-modal__unit-option--active', isActive);
          b.setAttribute('aria-pressed', isActive);
        });
        switchEl.setAttribute('aria-checked', unit === 'cm');
        updateTable(unit);
      });
    });

    switchEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      const current = sizeChartPanel.querySelector('.size-guide-modal__unit-option[aria-pressed="true"]');
      const next = current?.dataset.unit === 'cm'
        ? sizeChartPanel.querySelector('.size-guide-modal__unit-option[data-unit="in"]')
        : sizeChartPanel.querySelector('.size-guide-modal__unit-option[data-unit="cm"]');
      next?.click();
    });
  }
}

if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
